import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import {
  LanguageOutlined as LanguageIcon,
  FingerprintOutlined as HashIcon,
  CategoryOutlined as TagIcon,
  LayersOutlined as LayersIcon,
  PhotoSizeSelectActualOutlined as ScaleIcon,
  ShoppingCartOutlined as ShoppingCartIcon,
  PublicOutlined as PublicIcon,
  FlagOutlined as FlagIcon,
} from '@material-ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useMetadataStyles } from './styles/metadata.styles';
import { MetadataField } from './types';
import { UrlHelper } from '../../utils/url-helper';
import { 
  QUERY_KEYS, 
  CACHE_TIMES, 
  METADATA_FIELDS,
  METADATA_FIELD_COLORS,
  METADATA_FIELD_TOOLTIP_DESCRIPTIONS,
} from './constants';
import { WebsiteViewService } from 'src/model/WebsiteViewService';
import { toNumber, toSafeString } from 'src/utils/string-helper';
import { GenericTooltip } from './components/GenericTooltip';

export const WebsiteMetadata = React.memo((): JSX.Element => {
  const classes = useMetadataStyles();
  const location = useLocation();
  const website = UrlHelper.getParamValue('website');
  const websiteId = toNumber(UrlHelper.getParamValue('websiteId'));

  // React Query to fetch metadata for the website
  const { data: metadata, isFetching: isLoading, error } = useQuery({
    queryKey: [QUERY_KEYS.WEBSITE_METADATA, website, websiteId],
    queryFn: () => new WebsiteViewService().getWebsiteMetadata(website || '', websiteId),
    enabled: !!website || !!websiteId,
    staleTime: CACHE_TIMES.STALE_TIME,
    cacheTime: CACHE_TIMES.CACHE_TIME,
    retry: 3
  });

  // Create metadata fields from fetched data
  const metadataFields: MetadataField[] = metadata ? [
    { field: METADATA_FIELDS.WEBSITE_ID, value: toSafeString(metadata.websiteId), icon: <HashIcon />, color: METADATA_FIELD_COLORS.WEBSITE_ID, tooltipDescription: METADATA_FIELD_TOOLTIP_DESCRIPTIONS.WEBSITE_ID },
    { field: METADATA_FIELDS.WEBSITE_TYPE, value: toSafeString(metadata.websiteType), icon: <LanguageIcon />, color: METADATA_FIELD_COLORS.WEBSITE_TYPE, tooltipDescription: METADATA_FIELD_TOOLTIP_DESCRIPTIONS.WEBSITE_TYPE },
    { field: METADATA_FIELDS.PROGRAM, value: toSafeString(metadata.programs?.join(',')), icon: <LayersIcon />, color: METADATA_FIELD_COLORS.PROGRAM, tooltipDescription: METADATA_FIELD_TOOLTIP_DESCRIPTIONS.PROGRAM },
    { field: METADATA_FIELDS.SUB_PROGRAM, value: toSafeString(metadata.subPrograms?.join(',')), icon: <LayersIcon />, color: METADATA_FIELD_COLORS.SUB_PROGRAM, tooltipDescription: METADATA_FIELD_TOOLTIP_DESCRIPTIONS.SUB_PROGRAM },
    { field: METADATA_FIELDS.SELECTION_SIZE, value: toSafeString(metadata.estSelectionSize), icon: <ScaleIcon />, color: METADATA_FIELD_COLORS.SELECTION_SIZE, tooltipDescription: METADATA_FIELD_TOOLTIP_DESCRIPTIONS.SELECTION_SIZE },
    { field: METADATA_FIELDS.PLATFORM, value: toSafeString(metadata.platform), icon: <ShoppingCartIcon />, color: METADATA_FIELD_COLORS.PLATFORM, tooltipDescription: METADATA_FIELD_TOOLTIP_DESCRIPTIONS.PLATFORM },
    { field: METADATA_FIELDS.OPERATING_COUNTRIES, value: toSafeString(metadata.operatingCountries?.join(',')), icon: <PublicIcon />, color: METADATA_FIELD_COLORS.OPERATING_COUNTRIES, tooltipDescription: METADATA_FIELD_TOOLTIP_DESCRIPTIONS.OPERATING_COUNTRIES },
    { field: METADATA_FIELDS.COUNTRY_OF_ORIGIN, value: toSafeString(metadata.countryOfOrigin), icon: <FlagIcon />, color: METADATA_FIELD_COLORS.COUNTRY_OF_ORIGIN, tooltipDescription: METADATA_FIELD_TOOLTIP_DESCRIPTIONS.COUNTRY_OF_ORIGIN }
  ] : [];

  const renderMetadataItem = (item: MetadataField & { color?: string }, index: number) => (
    <Grid item xs={12} sm={6} md={4} key={index}>
      <GenericTooltip
        title={item.field}
        description={item.tooltipDescription || ''}
        placement="top"
      >
        <Paper className={classes.metadataItemPaper} elevation={1}>
          <Box className={classes.metadataItem}>
            <Box className={classes.iconContainer}>
              {React.cloneElement(item.icon, { 
                fontSize: 'large',
                style: { color: item.color || METADATA_FIELD_COLORS.WEBSITE_ID }
              })}
            </Box>
            <Box className={classes.metadataContent}>
              <Typography variant="caption" className={classes.fieldLabel}>
                {item.field}
              </Typography>
              <Typography variant="body2" className={classes.fieldValue} style={{fontSize: '1rem'}} >
                {item.value}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </GenericTooltip>
    </Grid>
  );

  // Show loading state
  if (isLoading) {
    return (
      <Paper className={classes.paper}>
        <Box className={classes.header}>
          <Box className={classes.headerContent}>
            <LanguageIcon className={classes.headerIcon} />
            <Typography variant="h4" className={classes.headerTitle}>
              Website Metadata
            </Typography>
          </Box>
        </Box>
        <Box className={classes.content}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </Box>
      </Paper>
    );
  }

  // Show error state
  if (error || !metadata) {
    return (
      <Paper className={classes.paper}>
        <Box className={classes.header}>
          <Box className={classes.headerContent}>
            <LanguageIcon className={classes.headerIcon} />
            <Typography variant="h4" className={classes.headerTitle}>
              Website Metadata
            </Typography>
          </Box>
        </Box>
        <Box className={classes.content}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography color="textSecondary">
              {!website ? 'No website selected' : 'Failed to load metadata'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      {/* Header - Timeline Style */}
      <Box className={classes.header}>
        <Box className={classes.headerContent}>
          <LanguageIcon className={classes.headerIcon} />
          <Typography variant="h4" className={classes.headerTitle}>
            Website Metadata
          </Typography>
        </Box>
      </Box>

      {/* Content - Compact Grid Layout */}
      <Box className={classes.content}>
        <Grid container spacing={2}>
          {metadataFields.map((item: MetadataField & { color?: string }, index: number) => 
            renderMetadataItem(item, index)
          )}
        </Grid>
      </Box>
    </Paper>
  );
});

WebsiteMetadata.displayName = 'WebsiteMetadata';
